import type { CSSProperties } from 'vue'

import ErrorElement from '../../elements/error/ErrorElement.vue'
import { isElementType, type ElementType } from '../../elements/elementTypes'

export { FLASHCARD_BASE_HEIGHT, FLASHCARD_BASE_WIDTH } from './constants'
export type {
  CheckboxGroupElementInput,
  CheckboxGroupToggleDetail,
  ElementEventPayload,
  FlashcardElementInput,
  FlashcardSize,
  FlipAnimation,
  RenderableElement,
  ThemeContext,
} from './types'

import type {
  BaseElementInput,
  ElementEventPayload,
  FlashcardElementInput,
  FlashcardSize,
  RenderableElement,
  ThemeContext,
} from './types'

import { buildBaseStyle, buildFullScreenStyle } from './internal/style'

import type { ElementContext, ElementDefinition } from './definitions/elementDefinition'

const RUNTIME_ELEMENT_DEFINITIONS = import.meta.glob('../../elements/*/runtime.ts', {
  eager: true,
  import: 'default',
}) as Record<string, ElementDefinition<FlashcardElementInput>>

type RegistryEntry = {
  component: ElementDefinition<FlashcardElementInput>['component']
  toProps: (
    input: FlashcardElementInput,
    size: FlashcardSize,
    baseStyle: CSSProperties,
    theme: ThemeContext,
    context: ElementContext,
  ) => Record<string, unknown> | null
  estimateBox?: (
    input: FlashcardElementInput,
    size: FlashcardSize,
  ) => { widthPx: number; heightPx: number } | null
}

const isImageElement = (
  input: FlashcardElementInput,
): input is Extract<FlashcardElementInput, { type: 'image' }> => {
  const record = input as Record<string, unknown>
  return input.type === 'image' && typeof record.src === 'string'
}

const makeEntry = <T extends FlashcardElementInput>(def: ElementDefinition<T>): RegistryEntry => ({
  component: def.component,
  estimateBox: def.estimateBox
    ? (input, size) => {
        if (input.type !== def.type) return null
        return def.estimateBox?.(input as T, size) ?? null
      }
    : undefined,
  toProps: (input, size, baseStyle, theme, context) => {
    if (input.type !== def.type) return null
    return def.toProps(input as T, {
      size,
      baseStyle,
      theme,
      context,
    })
  },
})

const registry = Object.fromEntries(
  Object.values(RUNTIME_ELEMENT_DEFINITIONS).map((def) => [def.type, makeEntry(def)]),
) as Record<ElementType, RegistryEntry>

export const estimateElementBox = (input: FlashcardElementInput, size: FlashcardSize) => {
  const entry = isElementType(input.type) ? registry[input.type] : undefined
  const estimated = entry?.estimateBox?.(input, size) ?? null
  if (estimated && Number.isFinite(estimated.widthPx) && Number.isFinite(estimated.heightPx)) {
    return {
      widthPx: Math.max(0, estimated.widthPx),
      heightPx: Math.max(0, estimated.heightPx),
    }
  }

  // Sensible fallback to avoid special-casing every future element type.
  return {
    widthPx: size.width * 0.12,
    heightPx: size.height * 0.08,
  }
}

const buildError = (
  prefix: string,
  index: number,
  detail: string,
  style?: CSSProperties,
): RenderableElement => ({
  key: `${prefix}-${index}`,
  component: ErrorElement,
  props: { reason: detail, style },
})

type ResolveOptions = {
  emitElementEvent?: (payload: ElementEventPayload) => void
  includeEditorMeta?: boolean
}

export const resolveElements = (
  elements: FlashcardElementInput[] | undefined,
  prefix: string,
  size: FlashcardSize,
  theme: ThemeContext,
  options?: ResolveOptions,
): RenderableElement[] => {
  if (!Array.isArray(elements) || elements.length === 0) {
    return []
  }

  return elements.map((element, index) => {
    if (!element || typeof element !== 'object') {
      return buildError(prefix, index, `Invalid element at ${prefix}#${index}`)
    }

    if (!isElementType(element.type)) {
      return buildError(prefix, index, `Unsupported type ${String(element.type)}`)
    }

    const entry = registry[element.type]

    if (!entry) {
      return buildError(prefix, index, `Unsupported type ${element.type}`)
    }

    let baseStyle = buildBaseStyle(element as BaseElementInput)

    if (!baseStyle && isImageElement(element) && element.fullScreenImage) {
      baseStyle = buildFullScreenStyle(element as BaseElementInput)
    }

    if (!baseStyle) {
      return buildError(prefix, index, `Missing position for ${element.type}`)
    }

    const face: 'front' | 'back' = prefix === 'back' ? 'back' : 'front'

    const props = entry.toProps(element, size, baseStyle, theme, {
      face,
      index,
      key: `${prefix}-${index}`,
      emitElementEvent: options?.emitElementEvent,
    })

    if (!props) {
      return buildError(prefix, index, `Malformed data for type ${element.type}`, baseStyle)
    }

    ;(props as Record<string, unknown>)['data-flashcard-element'] = 'true'

    if (options?.includeEditorMeta) {
      const editorMeta = (element as Record<string, unknown>).__editor as
        | { id?: unknown; selected?: unknown; face?: unknown; editing?: unknown }
        | undefined
      if (editorMeta && typeof editorMeta.id === 'string') {
        ;(props as Record<string, unknown>)['data-editor-id'] = editorMeta.id
        ;(props as Record<string, unknown>)['data-editor-selected'] = editorMeta.selected
          ? 'true'
          : 'false'
        ;(props as Record<string, unknown>)['data-editor-editing'] = editorMeta.editing
          ? 'true'
          : 'false'
        ;(props as Record<string, unknown>)['data-editor-type'] = element.type
        ;(props as Record<string, unknown>)['data-editor-face'] = prefix
        const locked = (editorMeta as { locked?: unknown }).locked
        ;(props as Record<string, unknown>)['data-editor-locked'] = locked ? 'true' : 'false'
      }
    }

    return {
      key: `${prefix}-${index}`,
      component: entry.component,
      props,
    }
  })
}
